import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Announcement03Icon } from '@/components/icons/Announcement03Icon';
import { StyledCounter } from '@/components/notifications/StyledCounter/StyledCounter';
import { COLORS } from '@/styles/tokens/colors';

import { FeatureAnnouncementId } from '../../featureAnnouncements.config';
import { useFeatureAnnouncements } from '../../hooks/useFeatureAnnouncements';
import { FeatureAnnouncementCarouselModal } from '../FeatureAnnouncementCarouselModal/FeatureAnnouncementCarouselModal';

export interface FeatureAnnouncerProps {
  /** Whether the sidebar is expanded or collapsed */
  isExpanded: boolean;
  /** Current household ID for navigation context */
  householdId: string;
}

/**
 * Card component that displays in the sidebar to announce new features.
 * Shows a badge with count of unviewed announcements.
 * Clicking opens a modal with the first unviewed announcement.
 */
export function FeatureAnnouncer({
  isExpanded,
  householdId,
}: FeatureAnnouncerProps) {
  const { unviewedAnnouncements, unviewedCount, markMultipleAsViewed } =
    useFeatureAnnouncements();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLearnMore = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(
    (viewedIds: FeatureAnnouncementId[]) => {
      // Mark all displayed announcements as viewed when closing the carousel
      markMultipleAsViewed(viewedIds);
      setIsModalOpen(false);
    },
    [markMultipleAsViewed]
  );

  // Get the first unviewed announcement to determine what to show in the card
  const firstUnviewedAnnouncement = unviewedAnnouncements[0];

  // Don't render if there are no announcements to show
  if (unviewedCount === 0) {
    return null;
  }

  if (isExpanded) {
    return (
      <Stack px={1.5} pb={1.5}>
        <Stack
          p={1.5}
          spacing={0.5}
          bgcolor={COLORS.BLUE[50]}
          border={`1px solid ${COLORS.BLUE[100]}`}
          borderRadius="4px"
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Announcement03Icon size={20} color={COLORS.TEAL[700]} />
            <Typography variant="title-xs" color={COLORS.TEAL[700]}>
              New feature
            </Typography>
          </Stack>
          <Typography variant="title-xs">
            {firstUnviewedAnnouncement?.announcementTitle}
          </Typography>
          <Typography variant="body-xs" sx={{ pb: 0.5 }}>
            {firstUnviewedAnnouncement?.announcementBlurb}
          </Typography>
          <Button onClick={handleLearnMore} variant="primary" size="xs">
            Learn more
          </Button>
        </Stack>
        {isModalOpen && (
          <FeatureAnnouncementCarouselModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            announcements={unviewedAnnouncements}
            householdId={householdId}
          />
        )}
      </Stack>
    );
  }

  // Collapsed state - show only icon with badge
  return (
    <Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 1,
          cursor: 'pointer',
          borderRadius: 1,
        }}
        onClick={handleLearnMore}
      >
        <StyledCounter
          badgeContent={unviewedCount}
          color="error"
          show={true}
          sx={{
            '& .MuiBadge-badge': {
              right: 4,
              top: 4,
            },
          }}
        >
          <Announcement03Icon size={20} color={COLORS.TEXT.SECONDARY} />
        </StyledCounter>
      </Box>
      {isModalOpen && (
        <FeatureAnnouncementCarouselModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          announcements={unviewedAnnouncements}
          householdId={householdId}
        />
      )}
    </Stack>
  );
}
